import React, { useCallback, useEffect } from 'react'

import {
  Banner,
  BannerDescription,
  BannerEyebrow,
  BannerImage,
  BannerImageContainer,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  Box,
  Button,
  Card,
  FluidImage,
  Link,
  ProgressiveImage,
  Typography,
  useScreenClass,
  Icon,
  BoundedContent,
  GridContainer,
  Column,
  Row,
  BannerFootnote,
  Hidden,
  Visible,
} from '@vp/swan'

import { useLogger, useStyles } from '@vp/ubik-context'
import { CANONICAL_ID } from '@vp/digital-auth-lib-ubik'
import { PartnerAccountProducts, getPartnerAccountProducts } from '@vp/digital-paid-lib-ubik'
import {
  SearchComponent,
  SearchComponentInputModel,
  SearchComponentCTAModel,
  useSearchedDomainValue,
  useHasOwnDomainValue,
} from '@vp/digital-search-component-lib-ubik'
import { isProduction } from '@vp/digital-environment-lib-ubik'

import {
  trackWixFindMyDomainCTAClick,
  trackWixConnectDomainsCTAClick,
  trackCreateFreeVistaWixWebsiteMainCTAClick,
  trackCreateFreeVistaWixWebsiteKeyBenefitsCTAClick,
  trackCreateFreeVistaWixWebsiteHeroPretitleCTAClick,
  trackYelpPromoViewed,
} from './WebsShutdownAnalytics'
import { redirect } from '../../common/helpers/urlsHelper'
import { WEBS_SHUTDOWN_URL_ID } from '../../common/urlKeys'
import { urlParamsEnum } from './utils/urlParamsEnum'
import HeroSection from '../../common/components/WebsitesHeroSection'
import KeyBenefitsSection from '../../common/components/KeyBenefitsSection'
import { getWixPartnerConnectDomainBuildWebsiteUrl } from './utils/wixDomainsUrls'
import getResponsiveCloudinaryImage from '../../common/helpers/cloudinaryImageResizerHelper'
import { updateUrlWixPartnerGetStartedTnCAcceptedUrlsQueryParams } from './utils/updateUrlWixPartnerGetStartedTnCAcceptedUrlsQueryParams'

import { useSignedInAction } from '../../common/helpers/useSignedInAction'
import './WebsShutdownPage.scss'
import styles, { stylesheet } from './WebsShutdownPage.scss'
import { VPAuthIdentity } from '../../common/types/Types'
import YelpPromo from './YelpPromo'

export const YELP_BUSINESS_LINK =
  'https://business.yelp.com/campaign/vista/?promo=VPDC10D300&utm_source=Vista&utm_medium=partner&utm_campaign=VistaStartYourBusiness2024&utm_content=WebsShutdownPage'

const WebsShutdownPage = (pageContext: any) => {
  const logger = useLogger()
  useStyles(stylesheet)
  const isMobile = useScreenClass() === 'xs'

  const [domain, setDomain, resetDomain] = useSearchedDomainValue()
  const [, setHasOwnDomainValue, resetHasOwnDomainValue] = useHasOwnDomainValue()
  const fetchSiteList = async (locale: string, identity: VPAuthIdentity) => {
    const data = await getPartnerAccountProducts(identity.profile[CANONICAL_ID] as string)
    const redirectUrl = getConnectDomainUrl(data)
    redirect(redirectUrl)
    trackWixConnectDomainsCTAClick(redirectUrl)
  }

  const [, , initiateFetchSiteList] = useSignedInAction<string, void>(fetchSiteList, 'fetch-site-list-after-sign-in')

  useEffect(() => {
    setTimeout(() => {
      trackYelpPromoViewed()
    }, 100)
  }, [])

  const { locale, pageData: content, commonLocalizedUrls, urlWixPartnerGetStartedTnCAcceptedUrls, mainHeroContent, yelpPromoData } = pageContext

  const { wixDomainsSearchAndSuggestionsUrl } = commonLocalizedUrls
  const updatedWixGetStartedTnCAcceptedUrls = {
    prod: updateUrlWixPartnerGetStartedTnCAcceptedUrlsQueryParams(urlWixPartnerGetStartedTnCAcceptedUrls.prod, 'webs-shutdown-page', 'webs-shutdown-page'),
    qa: updateUrlWixPartnerGetStartedTnCAcceptedUrlsQueryParams(urlWixPartnerGetStartedTnCAcceptedUrls.qa, 'webs-shutdown-page', 'webs-shutdown-page'),
  }

  const updatedWixGetStartedTnCAcceptedUrl = isProduction() ? updatedWixGetStartedTnCAcceptedUrls.prod : updatedWixGetStartedTnCAcceptedUrls.qa

  const source = WEBS_SHUTDOWN_URL_ID
  const CONNECT_DOMAINS_KEY = 'wixConnectDomainsLink'

  const connectDomainsLink = content.newDomainsHero.heroMetadata?.find(({ key }: { key: string }) => key === CONNECT_DOMAINS_KEY)

  let querySiteId: string = null
  if (typeof window !== 'undefined' && window.location?.search) {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    querySiteId = urlParams?.get(urlParamsEnum.SITE_ID)?.toLowerCase() ?? urlParams?.get(urlParamsEnum.UPGRADE_SITE_ID)?.toLowerCase()
  }

  const handleWixFindMyDomainNavigation = (navigateWithoutData = false) => {
    if (navigateWithoutData === true) {
      resetDomain()
      resetHasOwnDomainValue()
    }
    setHasOwnDomainValue(false)
    logger.debug('Clicking Webs Shutdown Find My domain link')
    let url = wixDomainsSearchAndSuggestionsUrl
    url += `?${urlParamsEnum.REQUESTOR}=` + source
    if (querySiteId) {
      url += `&${urlParamsEnum.UPGRADE_SITE_ID}=${querySiteId}`
    }
    trackWixFindMyDomainCTAClick(url)
    redirect(url)
  }

  const handleSearchCTA = useCallback(
    (domain: any) => {
      setDomain(domain)
      handleWixFindMyDomainNavigation(false)
    },
    [handleWixFindMyDomainNavigation],
  )

  const ctaProps: SearchComponentCTAModel = {
    onClickHandler: handleSearchCTA,
  }

  const inputProps: SearchComponentInputModel = {
    ariaLabel: content.newDomainsHero.actions[1].title,
    textInputPlaceholder: content.newDomainsHero.secondaryCtaDescription,
    value: domain,
    setValue: setDomain,
  }

  const handleGetStartedNavigation = (ctaType?: any) => {
    try {
      logger.debug('Clicking Webs Shutdown Get Started to Wix IntroFunnel Navigate')

      let url = updatedWixGetStartedTnCAcceptedUrl
      if (url === undefined) {
        logger.error('Error Clicking Webs Shutdown Get Started to Wix IntroFunnel Navigate Url is undefined', null)
        return
      }

      if (isMobile) {
        url += '&deviceType=mobile'
      }

      switch (ctaType) {
        case 'heroPretitle':
          trackCreateFreeVistaWixWebsiteHeroPretitleCTAClick(url)
          break
        case 'keyBenefits':
          trackCreateFreeVistaWixWebsiteKeyBenefitsCTAClick(url)
          break
        default:
          trackCreateFreeVistaWixWebsiteMainCTAClick(url)
      }

      redirect(url)
    } catch (error) {
      logger.error('Error handling Get Started Navigation:', error)
    }
  }

  const handleWixConnectDomainsNavigation = () => {
    logger.debug('Clicking Webs Shutdown Connect Domains link')
    setHasOwnDomainValue(true)
    initiateFetchSiteList(locale)
  }

  const getConnectDomainUrl = (productDetails: PartnerAccountProducts): string => {
    const numberOfSites = productDetails?.wixSites?.length
    let siteId: string
    if (numberOfSites === 1) {
      siteId = productDetails?.wixSites[0].id
    }
    const wixPartnerConnectDomainUrl: string = getWixPartnerConnectDomainBuildWebsiteUrl(locale, source, domain, source, siteId)
    return wixPartnerConnectDomainUrl
  }
  return (
    <>
      {/* Eyebrow */}
      {content.eyebrow && (
        <BoundedContent>
          <Box className={styles.websShutdownPageEyebrow}>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography fontSize="standard" fontWeight="normal">
                {content.eyebrow.title}
              </Typography>

              <Typography fontSkin="title-section" textColor="warning">
                {content.eyebrow.valueText}
              </Typography>
            </Box>

            <Typography fontSkin="title-item">{content.eyebrow.description}</Typography>
          </Box>
        </BoundedContent>
      )}

      {/* Hero Pretitle */}
      {content.heroPretitle && (
        <BoundedContent>
          <Card bordered evenHeight darkMode className={styles.websShutdownPagePrehero} mx={0}>
            <Box className={styles.websShutdownPageContent}>
              <Icon iconType="polish" size="standard" skin="accent" />

              <Typography fontSkin="title-subsection">{content.heroPretitle.description}</Typography>
            </Box>

            <Button style={{ wordBreak: 'normal' }} skin="primary" iconPosition="right" onClick={() => handleGetStartedNavigation('heroPretitle')}>
              {content.heroPretitle.valueText}

              <Icon iconType="arrowRight" size="standard" skin="standard" />
            </Button>
          </Card>
        </BoundedContent>
      )}

      {/* Hero */}
      {mainHeroContent && (
        <HeroSection pageContext={pageContext} content={mainHeroContent} isWebsShutdown handleGetStartedNavigation={() => handleGetStartedNavigation('hero')} />
      )}
      {content && (
        <BoundedContent padding={0} pb={3}>
          <GridContainer>
            <Row>
              <Column span={6} padding={0} pr={2} display="flex">
                <Banner id="digital-search-component-section" variant="full-width-image" bgc="info">
                  <BannerTextContainer pt={6} display="flex">
                    <BannerText>
                      <BannerEyebrow>
                        <ProgressiveImage placeholder={content.gwsPromo.features[2].mediaUrl} src={content.gwsPromo.features[2].mediaUrl}>
                          {(image) => (
                            <FluidImage
                              style={{ maxWidth: '10em' }}
                              src={image.src}
                              width="180"
                              height="31"
                              alt={content.newDomainsHero.backgroundImage.media.fileName}
                            />
                          )}
                        </ProgressiveImage>
                      </BannerEyebrow>
                      <BannerTitle mb={6} style={{ fontSize: '30px' }}>
                        {content.newDomainsHero.title}
                      </BannerTitle>
                      <BannerDescription mb={6}>{content.newDomainsHero.descriptionLong}</BannerDescription>
                      <SearchComponent ctaProps={ctaProps} inputProps={inputProps} />
                      <BannerFootnote>
                        {connectDomainsLink.title}{' '}
                        <Link
                          onClick={() => {
                            handleWixConnectDomainsNavigation()
                          }}
                        >
                          {connectDomainsLink.description}
                        </Link>
                      </BannerFootnote>
                    </BannerText>
                  </BannerTextContainer>
                  <Hidden xs>
                    <BannerImageContainer>
                      <BannerImage>
                        <FluidImage
                          srcSet={getResponsiveCloudinaryImage(content.newDomainsHero.mediaList[2].media.secureUrl)}
                          alt={content.newDomainsHero.mediaList[2].altText}
                          style={{
                            objectFit: 'cover',
                            objectPosition: 'right',
                          }}
                        />
                      </BannerImage>
                    </BannerImageContainer>
                  </Hidden>
                  <Visible xs>
                    <BannerImageContainer className="webs-shutdown-page__domains-image" backgroundColor="info" darkMode>
                      <BannerImage>
                        <FluidImage
                          srcSet={getResponsiveCloudinaryImage(content.newDomainsHero.mediaList[1].media.secureUrl)}
                          alt={content.newDomainsHero.mediaList[1].altText}
                          style={{ objectFit: 'contain' }}
                        />
                      </BannerImage>
                    </BannerImageContainer>
                  </Visible>
                </Banner>
              </Column>
              <Column span={6} padding={0} pl={2} display="flex">
                <YelpPromo content={yelpPromoData} url={YELP_BUSINESS_LINK} />
              </Column>
            </Row>
          </GridContainer>
        </BoundedContent>
      )}
      {/* benefits key Component */}
      {content.keyBenefits && (
        <BoundedContent as="article" backgroundColor="warm-white" py={{ xs: 6, sm: 6, md: 9 }} id="key-benefits">
          <KeyBenefitsSection
            content={content.keyBenefits}
            isWebsShutdown
            handleGetStartedNavigation={() => handleGetStartedNavigation('keyBenefits')}
            accessibility={content.accessibility}
          />
        </BoundedContent>
      )}
    </>
  )
}

export default WebsShutdownPage
